<template>
  <div class="chat-panel-wrapper">
    <div class="chat-messages-area pt-4 px-4" ref="scrollableList"
      @scroll="onScroll">
        <Loader v-if="loading" />
        <v-list v-else>
          <template v-for="(message) in messages">
            <v-list-item v-if="message.senderId != user?.id" :key="message.id">
              <v-list-item-avatar class="align-self-start mr-2">
                <v-avatar size="40">
                  <v-img :src="message.senderPhotoUrl"></v-img>
                </v-avatar>
              </v-list-item-avatar>
              <v-list-item-content :class="['received-message', { 'received-message--staff': message.senderRole === 'staff' }]">
                <v-card :color="message.senderRole === 'staff' ? 'primary' : 'success'" class="flex-none no-shadow">
                  <v-card-text class="white--text pa-2 d-flex flex-column no-shadow">
                    <span class="text-caption">{{ message.senderName }} <span class="font-italic">({{ message.senderRole }})</span></span>
                    <span class="align-self-start text-subtitle-1">{{ message.text }}</span>
                    <span class="text-caption font-italic align-self-end">{{ message.createdAtDisplay }}</span>
                    <ChatMedia v-if="message?.medias?.length > 0" :data="{ medias: message.medias }"></ChatMedia>
                  </v-card-text>
                </v-card>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-else :key="message.id">
              <v-list-item-content class="sent-message justify-end">
                <v-card color="primary" class="flex-none no-shadow">
                  <v-card-text class="white--text pa-2 d-flex flex-column no-shadow">
                    <span class="text-subtitle-1 chat-message">{{ message.text }}</span>
                    <span class="text-caption font-italic align-self-start">{{ message.createdAtDisplay }}</span>
                    <ChatMedia v-if="message?.medias?.length > 0" :data="{ medias: message.medias }"></ChatMedia>
                  </v-card-text>
                </v-card>
              </v-list-item-content>
              <v-list-item-avatar class="align-self-start ml-2">
                <v-img :src="user.profilePicUrl"></v-img>
              </v-list-item-avatar>
            </v-list-item>
          </template>
        </v-list>
        <div ref="bottomElement"></div>
    </div>

    <div class="messages-form p-4">
      <v-textarea v-model="message" :disabled="isFileUploading || loading" v-on:keyup.enter="sendMessage(null)"
        :label="'Enter your message here'" rows="3" max-rows="5" no-resize></v-textarea>

      <v-btn width="100px" :disabled="!message?.length || isMessageInvalid"
        :loading="isFileUploading || isSending || loading" class="btn-default inline-block float-right"
        @click="sendMessage(null)">
        {{ $t('GeneralTranslations.Send') }}
      </v-btn>

      <div class="float-left">
        <ChatMediaUploader :filePath="filePath" @onFileStartUpload="onFileStartUpload" @onFileUploaded="onFileUploaded"
          @onImageSelected="onImageSelected"></ChatMediaUploader>
      </div>
    </div>
  </div>
</template>

<script>
import * as firebase from "firebase/app";
import { mapGetters } from "vuex";
import { nextTick } from 'vue';
import { localizedDate } from "@/utils/localizedDate";
import ChatMedia from '@/components/chat/ChatMedia.vue'
import ChatMediaUploader from '@/components/chat/ChatMediaUploader.vue'
import Loader from '@/components/loader/Loader.vue';
import moment from "moment";
import { DEFAULT_AVATAR_WHITE_200x200 } from '@/consts/default-avatar';

export default {
  name: "HomeChatPanel",
  components: { ChatMedia, ChatMediaUploader, Loader },
  props: {
    data: Object
  },
  data() {
    return {
      loading: true,
      message: null,
      messages: [],
      onMessagesUnsubscribe: null,
      isFileUploading: false,
      isSending: false,
      isAtBottom: true
    }
  },

  computed: {
    facility() {
      return this.$store.state.authModule.currentFacilityData
    },
    filePath() {
      return `${this.facility.id}/channel_chat_images/${this.data.familyMember.id}/${this.user.id}`;
    },
    isMessageInvalid() {
      return (!this.message || this.message.trim() === "") && !this.isFileUploading;
    },
    ...mapGetters({
      user: "user"
    })
  },

  async created() {
    await this.initChat()
  },

  updated() {
    if (this.isAtBottom) {
      this.scrollToLastItem();
    }
  },

  mounted() {
    const observer = new IntersectionObserver(([entry]) => {
      this.isAtBottom = entry.isIntersecting;
    }, { threshold: 1 });

    if (this.$refs.bottomElement) {
      observer.observe(this.$refs.bottomElement);
    }
  },

  beforeDestroy() {
    this.unsubscribeFromMessages()
  },

  methods: {
    async initChat() {
      this.messages = await this.$store.dispatch('getHomeChatMessages', {
        channel: this.data.channel,
        facilityId: this.facility.id,
      })

      await this.$store.dispatch('readHomeChatMessages', {
        channel: this.data.channel,
        facilityId: this.facility.id,
      })

      await this.subscribeOnMessages()
      await nextTick()
      this.scrollToLastItem()
      this.loading = false
    },

    async sendMessage(media = null) {
      if (this.isMessageInvalid && !media) return;

      this.isSending = true
      const newMessage = {
        channel: this.data.channel,
        facilityId: this.facility.id,
        orgId: this.facility.orgId,
        senderId: this.user.id,
        senderName: this.user.name,
        senderPhotoUrl: this.user?.profilePicUrl || this.user?.thumbnailUrl || DEFAULT_AVATAR_WHITE_200x200,
        senderRole: 'staff',
        text: this.message,
        read: false,
        readDateTime: null,
        receiverId: null,
        residentId: null,
        chatId: '',
        medias: [],
      }

      if (media) {
        newMessage.medias = [media]
      }

      this.message = ''
      await this.$store.dispatch("createHomeChatMessage", newMessage);
      this.isSending = false
    },

    onFileStartUpload() {
      this.isFileUploading = true;
    },

    onScroll() {
      const el = this.$refs.scrollableList;
      if (!el) return;
      this.isAtBottom = (el.scrollTop + el.clientHeight >= el.scrollHeight - 10);
    },

    async onFileUploaded(file) {
      if (!file?.url) {
        throw Error('Unable to upload file')
      }
      const media = { type: file.type, url: file.url }
      await this.sendMessage(media)
      this.isFileUploading = false
    },

    async onImageSelected(image) {
      if (!image?.image) {
        throw Error('Unable to select image')
      }
      const media = { type: 'image', url: image.image }
      await this.sendMessage(media)
    },

    async subscribeOnMessages() {
      const messagesQuery = firebase.firestore().collection("homeChatsMessage")
        .where('channel', '==', this.data.channel)
        .where("facilityId", "==", this.facility.id)
        .where("createdAt", ">=", firebase.firestore.Timestamp.fromDate(new Date()))
        .orderBy("createdAt", "desc")

      this.onMessagesUnsubscribe = await messagesQuery
        .onSnapshot((snapshot) => {
          snapshot.docChanges().forEach(async (change) => {
            if (change.type === "added") {
              if (this.messages.some(m => m.id === change.doc.id)) return;
              const data = change.doc.data()
              const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt
              const message = {
                ...data,
                id: change.doc.id,
                createdAtDisplay: moment(createdAt).isSame(moment(), 'day')
                  ? localizedDate(createdAt, this.facility.timeFormat)
                  : localizedDate(createdAt),
                user: {
                  id: data.senderId || '',
                  firstName: data.senderName || '',
                  profileImage: data.senderPhotoUrl || '',
                },
              }
              this.messages.push(message)
              this.scrollToLastItem()
              this.$store.dispatch('readHomeChatMessages', {
                channel: this.data.channel,
                facilityId: this.facility.id,
              })
            }

            if (change.type === "removed") {
              this.messages = this.messages.filter(x => x.id !== change.doc.id)
            }
          })
        })
    },

    unsubscribeFromMessages() {
      if (this.onMessagesUnsubscribe) {
        this.onMessagesUnsubscribe()
      }
    },

    scrollToLastItem() {
      this.$nextTick(() => {
        setTimeout(() => {
          const bottomElement = this.$refs.bottomElement;
          if (bottomElement) {
            bottomElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
          }
        }, 500);
      });
    }
  }
}
</script>

<style scoped lang="scss">
.v-card {
  padding: initial !important;
  min-height: initial !important;
  background-color: #fff !important;
  box-shadow: none !important;
}

.chat-panel-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  width: 100%;
}

.chat-messages-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.messages-form {
  flex-shrink: 0;
  width: 100%;
  padding-bottom: 4px;
}

::v-deep .messages-form .v-textarea textarea {
  min-height: 72px !important;
  resize: none;
}

.no-shadow {
  box-shadow: none !important;
}

.chat-message {
  display: unset !important;
  white-space: break-spaces;
}

.flex-none {
  flex: unset;
}

.received-message,
.sent-message {
  word-break: break-word;
  cursor: text;
}

.received-message::after {
  content: ' ';
  position: absolute;
  width: 0;
  height: 0;
  left: 54px;
  right: auto;
  top: 12px;
  bottom: auto;
  border: 12px solid;
  border-color: #4caf50 transparent transparent transparent;
}

.received-message--staff::after {
  border-color: #3f51b5 transparent transparent transparent;
}

.sent-message::after {
  content: ' ';
  position: absolute;
  width: 0;
  height: 0;
  left: auto;
  right: 54px;
  top: 12px;
  bottom: auto;
  border: 12px solid;
  border-color: #3f51b5 transparent transparent transparent;
}


::v-deep .v-card {
  box-shadow: none!important;
}
</style>
